import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storageApi } from '../utils/api';
import { customerApi } from '../utils/customerApi';
import { StorageData } from '../types/storage';
import { Customer, Vehicle } from '../types/customer';
import { format } from 'date-fns';
import { formatPlateForInput, validatePlate } from '../utils/plateFormatter';

interface Props {
  id?: string;
  customerId?: string;
  onClose: () => void;
}

const StorageForm: React.FC<Props> = ({ id, customerId, onClose }) => {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [storage, setStorage] = useState<StorageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [plakaError, setPlakaError] = useState<string>('');
  const [noError, setNoError] = useState<string>('');
  const [ebatError, setEbatError] = useState<string>('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(customerId || '');
  const [customerQuery, setCustomerQuery] = useState('');
  const [comboOpen, setComboOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const comboRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState<number>(0);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerTelefon, setNewCustomerTelefon] = useState('');
  const [newCustomerPlaka, setNewCustomerPlaka] = useState('');
  const [newCustomerArac, setNewCustomerArac] = useState('');
  const [newCustomerError, setNewCustomerError] = useState('');

  useEffect(() => {
    const init = async () => {
      if (id) {
        await loadStorage();
      } else {
        setLoading(false);
      }
      await loadCustomers();
    };
    init();
  }, [id]);

  const loadCustomers = async () => {
    try {
      const response = await customerApi.getAll();
      if (response.data?.data) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const fillCustomerFields = (customer: Customer, vehicleIdx: number) => {
    const musteriInput = formRef.current?.querySelector<HTMLInputElement>('#musteri');
    const telefonInput = formRef.current?.querySelector<HTMLInputElement>('#telefon');
    const plakaInput = formRef.current?.querySelector<HTMLInputElement>('#plaka');
    const aracInput = formRef.current?.querySelector<HTMLInputElement>('#arac');
    if (musteriInput) musteriInput.value = customer.ad;
    if (telefonInput) telefonInput.value = customer.telefon;
    if (customer.araclar && customer.araclar[vehicleIdx]) {
      if (plakaInput) plakaInput.value = customer.araclar[vehicleIdx].plaka;
      if (aracInput) aracInput.value = customer.araclar[vehicleIdx].arac;
    }
  };

  const customerLabel = (c: Customer) =>
    `${c.ad}${c.araclar?.length > 0 ? ` - ${c.araclar.map(v => v.plaka).join(', ')}` : ''}`;

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomerId(customer._id);
    setSelectedVehicleIndex(0);
    fillCustomerFields(customer, 0);
    setCustomerQuery(customerLabel(customer));
    setComboOpen(false);
  };

  const handleCustomerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Seçim varken yazmaya başlamak veya input'u silmek seçimi temizler
    if (selectedCustomerId) {
      setSelectedCustomerId('');
      setSelectedVehicleIndex(0);
    }
    setCustomerQuery(e.target.value);
    setActiveIndex(0);
    setComboOpen(true);
  };

  const handleCustomerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!comboOpen) {
        setComboOpen(true);
        return;
      }
      setActiveIndex((i) => Math.min(i + 1, Math.max(visibleCustomers.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault(); // formu göndermesin
      if (comboOpen && visibleCustomers[activeIndex]) {
        handleSelectCustomer(visibleCustomers[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (comboOpen) {
        e.preventDefault();
        e.stopPropagation(); // modal'ı kapatmasın
        setComboOpen(false);
      }
    }
  };

  // Seçili müşteri (edit modu, yeni oluşturulan, prop) input'ta görünsün
  useEffect(() => {
    if (!selectedCustomerId) return;
    const c = customers.find((x) => x._id === selectedCustomerId);
    if (c) setCustomerQuery(customerLabel(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, customers]);

  // Dışarı tıklayınca liste kapansın
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setComboOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const handleVehicleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = parseInt(e.target.value);
    setSelectedVehicleIndex(idx);
    const customer = customers.find(c => c._id === selectedCustomerId);
    if (customer) {
      fillCustomerFields(customer, idx);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) {
      setNewCustomerError('Müşteri adı zorunludur');
      return;
    }
    try {
      setNewCustomerError('');
      const araclar: Vehicle[] = [];
      if (newCustomerPlaka.trim() || newCustomerArac.trim()) {
        araclar.push({
          plaka: newCustomerPlaka.toUpperCase().replace(/\s+/g, ''),
          arac: newCustomerArac
        });
      }
      const response = await customerApi.create({
        ad: newCustomerName.trim(),
        telefon: newCustomerTelefon,
        araclar
      });
      if (response.data?.data) {
        const created = response.data.data;
        await loadCustomers();
        setSelectedCustomerId(created._id);
        const musteriInput = formRef.current?.querySelector<HTMLInputElement>('#musteri');
        const telefonInput = formRef.current?.querySelector<HTMLInputElement>('#telefon');
        const plakaInput = formRef.current?.querySelector<HTMLInputElement>('#plaka');
        const aracInput = formRef.current?.querySelector<HTMLInputElement>('#arac');
        if (musteriInput) musteriInput.value = created.ad;
        if (telefonInput) telefonInput.value = created.telefon || '';
        if (araclar.length > 0) {
          if (plakaInput) plakaInput.value = araclar[0].plaka;
          if (aracInput) aracInput.value = araclar[0].arac;
        }
        setShowNewCustomerForm(false);
        setNewCustomerName('');
        setNewCustomerTelefon('');
        setNewCustomerPlaka('');
        setNewCustomerArac('');
      }
    } catch (err: any) {
      setNewCustomerError(err.response?.data?.error || 'Müşteri oluşturulamadı');
    }
  };

  const loadStorage = async () => {
    try {
      const response = await storageApi.getById(id!);
      if (response.data?.data) {
        setStorage(response.data.data);
        if (response.data.data.cariKartId) {
          setSelectedCustomerId(response.data.data.cariKartId);
        }
      }
    } catch (err) {
      setError('Kayıt yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setError('');
      setNoError('');
      setEbatError('');
      const formData = new FormData(e.currentTarget);
      const plaka = formData.get('plaka') as string;
      const ebat = formData.get('ebat') as string;
      const noValue = formData.get('no') as string;

      if (!validatePlate(plaka)) {
        setPlakaError('Geçerli bir plaka giriniz (Örnek: 34ABC123)');
        return;
      }

      const data: Record<string, any> = {
        musteri: formData.get('musteri') as string,
        depo: formData.get('depo') as string,
        ebat: ebat.toUpperCase(),
        marka: formData.get('marka') as string,
        adet: parseInt(formData.get('adet') as string),
        arac: formData.get('arac') as string,
        plaka: plaka.toUpperCase(),
        telefon: formData.get('telefon') as string,
        not: formData.get('not') as string,
        tarih: formData.get('tarih') as string,
        no: noValue ? parseInt(noValue) : undefined
      };

      if (selectedCustomerId) {
        data.cariKartId = selectedCustomerId;
      }

      if (id) {
        await storageApi.update(id, data);
      } else {
        await storageApi.create(data);
      }
      onClose();
    } catch (err: any) {
      if (err.response?.data?.error === 'Bu numara zaten kullanımda') {
        setNoError('Bu numara zaten kullanımda');
      } else {
        setError('Kayıt güncellenirken bir hata oluştu');
      }
    }
  };

  const handlePlakaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPlate = formatPlateForInput(e.target.value);
    e.target.value = formattedPlate;
    setPlakaError('');
  };

  const handleEbatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    e.target.value = value;
    setEbatError('');
  };

  const MAX_SUGGESTIONS = 50;
  const filteredCustomers = (() => {
    // Seçim varken tam liste göster; yazılan metin yalnız seçim yokken filtredir
    const raw = selectedCustomerId ? '' : customerQuery;
    const q = raw.trim().toLocaleLowerCase('tr');
    if (!q) return customers;
    const qPlate = raw.trim().toLowerCase().replace(/\s+/g, '');
    return customers.filter((c) =>
      c.ad.toLocaleLowerCase('tr').includes(q) ||
      (c.araclar || []).some((a) =>
        (a.plaka || '').toLowerCase().replace(/\s+/g, '').includes(qPlate)
      )
    );
  })();
  const visibleCustomers = filteredCustomers.slice(0, MAX_SUGGESTIONS);

  if (loading) return <div className="loading">Yükleniyor...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="storage-form">
      <h2>{id ? 'Lastik Kaydını Düzenle' : 'Yeni Lastik Kaydı'}</h2>
      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label htmlFor="customer" style={{ margin: 0 }}>Müşteri Seç</label>
            <button
              type="button"
              onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
              style={{
                background: 'none',
                border: 'none',
                color: '#4f7cff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                padding: '2px 0'
              }}
            >
              {showNewCustomerForm ? 'İptal' : '+ Yeni Müşteri'}
            </button>
          </div>
          {!showNewCustomerForm && (
            <div className="combobox" ref={comboRef}>
              <input
                type="text"
                id="customer"
                role="combobox"
                aria-expanded={comboOpen}
                aria-controls="customer-listbox"
                aria-autocomplete="list"
                aria-label="Müşteri ara (ad veya plaka)"
                placeholder="Ad veya plaka yazın..."
                value={customerQuery}
                onChange={handleCustomerInputChange}
                onFocus={() => setComboOpen(true)}
                onKeyDown={handleCustomerKeyDown}
                autoComplete="off"
              />
              {comboOpen && (
                <ul className="combobox-list" id="customer-listbox" role="listbox">
                  {visibleCustomers.length === 0 && (
                    <li className="combobox-empty">Müşteri bulunamadı</li>
                  )}
                  {visibleCustomers.map((c, idx) => (
                    <li
                      key={c._id}
                      role="option"
                      aria-selected={c._id === selectedCustomerId}
                      className={
                        'combobox-option' +
                        (idx === activeIndex ? ' active' : '') +
                        (c._id === selectedCustomerId ? ' selected' : '')
                      }
                      onMouseDown={(e) => {
                        e.preventDefault(); // input odağını kaybetmesin
                        handleSelectCustomer(c);
                      }}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      {customerLabel(c)}
                    </li>
                  ))}
                  {filteredCustomers.length > MAX_SUGGESTIONS && (
                    <li className="combobox-empty">Daha fazlası için yazın...</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Araç seçimi - birden fazla araç varsa */}
        {!showNewCustomerForm && selectedCustomerId && (() => {
          const sc = customers.find(c => c._id === selectedCustomerId);
          return sc && sc.araclar?.length > 1 ? (
            <div className="form-group">
              <label htmlFor="vehicle">Araç Seç</label>
              <select
                id="vehicle"
                value={selectedVehicleIndex}
                onChange={handleVehicleChange}
              >
                {sc.araclar.map((v, idx) => (
                  <option key={idx} value={idx}>
                    {v.plaka} - {v.arac || 'Tanımsız'}
                  </option>
                ))}
              </select>
            </div>
          ) : null;
        })()}

        {/* Yeni Müşteri Formu */}
        {showNewCustomerForm && (
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '0.9rem' }}>Yeni Müşteri</div>
            {newCustomerError && (
              <div style={{ color: 'red', fontSize: '0.8rem', marginBottom: '8px' }}>{newCustomerError}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                type="text"
                placeholder="Müşteri Adı *"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
              />
              <input
                type="tel"
                placeholder="Telefon"
                value={newCustomerTelefon}
                onChange={(e) => setNewCustomerTelefon(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Plaka (34ABC123)"
                  value={newCustomerPlaka}
                  onChange={(e) => setNewCustomerPlaka(e.target.value)}
                  style={{ flex: 1, textTransform: 'uppercase' }}
                />
                <input
                  type="text"
                  placeholder="Araç (BMW F30)"
                  value={newCustomerArac}
                  onChange={(e) => setNewCustomerArac(e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleCreateCustomer}
                style={{ alignSelf: 'flex-start' }}
              >
                Müşteri Oluştur
              </button>
            </div>
          </div>
        )}
        <div className="form-group">
          <label htmlFor="musteri">Müşteri</label>
          <input
            type="text"
            id="musteri"
            name="musteri"
            defaultValue={storage?.musteri}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="depo">Depo</label>
          <input
            type="text"
            id="depo"
            name="depo"
            defaultValue={storage?.depo}
          />
        </div>
        <div className="form-group">
          <label htmlFor="tarih">Tarih</label>
          <input
            type="date"
            id="tarih"
            name="tarih"
            defaultValue={storage?.tarih ? format(new Date(storage.tarih), 'yyyy-MM-dd') : undefined}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="ebat">Ebat</label>
          <input
            type="text"
            id="ebat"
            name="ebat"
            defaultValue={storage?.ebat}
            onChange={handleEbatChange}
            placeholder="195/65R15"
            maxLength={9}
            required
            style={{ textTransform: 'uppercase' }}
          />
          {ebatError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{ebatError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="marka">Marka</label>
          <input
            type="text"
            id="marka"
            name="marka"
            defaultValue={storage?.marka}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="adet">Adet</label>
          <input
            type="number"
            id="adet"
            name="adet"
            defaultValue={storage?.adet}
            required
            min="1"
          />
        </div>
        <div className="form-group">
          <label htmlFor="arac">Araç</label>
          <input
            type="text"
            id="arac"
            name="arac"
            defaultValue={storage?.arac}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="plaka">Plaka</label>
          <input
            type="text"
            id="plaka"
            name="plaka"
            defaultValue={storage?.plaka}
            onChange={handlePlakaChange}
            maxLength={9}
            placeholder="34ABC123"
            required
            style={{ textTransform: 'uppercase' }}
          />
          {plakaError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{plakaError}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="telefon">Telefon</label>
          <input
            type="tel"
            id="telefon"
            name="telefon"
            defaultValue={storage?.telefon}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="not">Not</label>
          <textarea
            id="not"
            name="not"
            defaultValue={storage?.not}
          />
        </div>
        {id && (
          <div className="form-group">
            <label htmlFor="no">No</label>
            <input
              type="text"
              id="no"
              name="no"
              defaultValue={storage?.no?.toString()}
              readOnly
              style={{ backgroundColor: '#f8f9fa' }}
            />
            {noError && <div className="error-message" style={{ color: 'red', fontSize: '0.8rem' }}>{noError}</div>}
          </div>
        )}
        <div className="actions">
          <button type="submit" className="btn btn-primary me-2">
            {id ? 'Güncelle' : 'Kaydet'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            İptal
          </button>
        </div>
      </form>
    </div>
  );
};

export default StorageForm; 